import axios from "axios";
import { useContext, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";
import { shouldNotRetry404 } from "src/config/react-query";
import ChatContext from "src/reusable/Chat/context/ChatContext";
import {
  AssistantResponseSources,
  EMPTY_PROVENANCE,
  ProvenanceCapabilities,
} from "../types/provenance";
import { useChatStore } from "../stores/ChatStore";

/**
 * The dialogue id provenance consumers should query for — the surrounding
 * chat's ChatContext first, the global ChatStore as fallback.
 *
 * The context is per-surface: every ChatContainer (standalone /assistant, AA
 * Projects' SidebarChatPanel, product chats) provides its own dialogueId.
 * ChatStore.activeDialogueId is a GLOBAL set only by the standalone page and
 * product chats — the projects workspace never writes it, so consumers that
 * read only the store render provenance-less there (grey unnumbered cites on
 * reload, dead pills). Context-first also keeps two coexisting surfaces from
 * bleeding into each other.
 */
export const useProvenanceDialogueId = (): number | null => {
  const ctxDialogueId = useContext(ChatContext)?.dialogueId;
  const storeDialogueId = useChatStore((s) => s.activeDialogueId);
  return ctxDialogueId ?? storeDialogueId;
};

/** Query key for the (global) capability-discovery query. */
export const PROVENANCE_CAPABILITIES_QUERY_KEY = [
  "provenance",
  "capabilities",
] as const;

/**
 * True when the payload's schema_version is one this FE build understands
 * (major version 1 — the contract only breaks compatibility on a major bump).
 * A missing version is treated as recognized: pre-versioning payloads are
 * handled by the capability gate, not by this defensive check.
 */
export const isRecognizedProvenanceSchema = (
  schemaVersion: string | null | undefined,
): boolean => schemaVersion == null || /^1\./.test(schemaVersion);

/**
 * Fetch the provenance capability manifest. A 404 (legacy backend without the
 * endpoint) rejects — callers treat any error as "render the legacy view",
 * never as a user-facing failure (contract: feature absence never breaks).
 */
export const fetchProvenanceCapabilities = () => {
  return axios
    .get<ProvenanceCapabilities>(
      `${nuclearn_api_url}/datasets/provenance/capabilities`,
    )
    .then((response) => response.data);
};

/**
 * React Query hook for `GET /datasets/provenance/capabilities` (#20423
 * close-gaps). Keyed globally (capabilities are per-backend, not per-dialogue)
 * with a ~15 minute staleTime + refetch-on-focus so a backend flag flip
 * propagates without a reload. `retry: shouldNotRetry404` — a 404 is the
 * *normal* legacy-backend state (fail fast to the legacy fallback), while a
 * transient network blip keeps the default retry budget so it can't lock the
 * session into legacy until the next window refocus.
 */
export const useProvenanceCapabilities = () => {
  return useQuery(
    PROVENANCE_CAPABILITIES_QUERY_KEY,
    fetchProvenanceCapabilities,
    {
      staleTime: 15 * 60 * 1000, // 15 minutes
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: shouldNotRetry404,
    },
  );
};

/**
 * Whether the capability manifest allows v2 provenance fetching. Pure so the
 * gate is unit-testable: v2 is ready only once the capabilities query has
 * SUCCEEDED and doesn't explicitly disable retrieval events. While
 * capabilities are still loading the gate stays closed (consumers already
 * render their loading/legacy states); an error — a 404 on a legacy backend
 * being the normal case — keeps v2 off so provenance endpoints are never
 * requested from a backend that lacks them.
 */
export const isProvenanceV2Ready = (capabilities: {
  isSuccess: boolean;
  data?: ProvenanceCapabilities;
}): boolean =>
  capabilities.isSuccess &&
  capabilities.data?.features?.retrieval_events !== false;

/**
 * Capability gate composed into every provenance hook's `enabled` (message +
 * dialogue scope here; the artifact-manifest hook in ArtifactService), so
 * every consumer (inline text, pills, contribution strip, citation links,
 * Sources panel) inherits it without having to remember to gate — a legacy
 * backend without the provenance endpoints can never be polled by them.
 */
export const useProvenanceV2Ready = (): boolean =>
  isProvenanceV2Ready(useProvenanceCapabilities());

/**
 * The one message whose finalization we're currently awaiting — the assistant
 * turn whose stream just ended. Citations persist a beat after stream end, so
 * ONLY this message needs the aggressive poll/refetch treatment; every other
 * (historical) message is final and polling it is pure waste. Before this
 * gate, EVERY zero-citation message polled 2s×30 on every dialogue open —
 * ~300 requests/minute on a 10-turn dialogue (#20423 audit 8).
 */
let hotProvenanceMessageId: string | null = null;
let hotProvenanceExpiry: ReturnType<typeof setTimeout> | null = null;

// The hot window must self-clear: it gates the dialogue-scope poll, which is
// NOT keyed by message, so a permanently-set flag would keep a genuinely
// zero-citation dialogue polling 2s×30 on every mount (the "audit 8" request
// bomb). ~65s outlasts the 60s poll bound; citations that persist sooner stop
// the poll via the citedCount predicate long before this fires.
const HOT_WINDOW_MS = 65_000;

/**
 * Mark the just-finalized assistant message as awaiting citation persist.
 *
 * Note: the flag is module-global, not keyed by dialogue — a turn finalizing
 * in one chat surface lets every other mounted dialogue's panel poll for the
 * same ~65s window (the dialogue-scope interval gates on "any message hot").
 * Bounded and self-clearing, so harmless today; key it by dialogueId if a
 * projects-pane + standalone-chat combo ever shows phantom polling.
 */
export const markProvenanceMessageHot = (messageId: string | null): void => {
  hotProvenanceMessageId = messageId;
  if (hotProvenanceExpiry != null) clearTimeout(hotProvenanceExpiry);
  hotProvenanceExpiry = null;
  if (messageId != null) {
    hotProvenanceExpiry = setTimeout(() => {
      hotProvenanceMessageId = null;
      hotProvenanceExpiry = null;
    }, HOT_WINDOW_MS);
  }
};

export const isProvenanceMessageHot = (
  messageId: string | null | undefined,
): boolean => messageId != null && messageId === hotProvenanceMessageId;

/**
 * True while ANY assistant turn is awaiting citation persist. The dialogue-
 * scope funnel isn't keyed by message, so it gates its poll on this rather than
 * a specific id — the hot window is short and self-clearing, so a session-wide
 * "is a turn finalizing" signal is enough to heal the inline numbering without
 * a reload.
 */
export const isAnyProvenanceMessageHot = (): boolean =>
  hotProvenanceMessageId != null;

/**
 * `refetchInterval` predicate for the provenance polls (exported for tests).
 * Polls every 2s while the query is HEALTHY and the covered turn is still in
 * its finalization window; stops on error or once the payload reports
 * `turn_settled` — the backend's explicit "end-of-turn persist landed" marker
 * (message scope: the target message; dialogue scope: the latest assistant
 * turn). One settled fetch IS the converged state, so polling past it was
 * pure waste (~33 polls per turn against /messages/provenance).
 *
 * It deliberately does NOT infer settledness from the payload CONTENTS. The
 * backend persists the Cited tier progressively (and AFTER the Read tier), so
 * "a citation appeared" can be a partial tier (#20423 — "cited but not in
 * sidebar"), and the DIALOGUE-scope query is CUMULATIVE across turns, so a
 * non-zero cited count can be entirely carried over from earlier turns. Only
 * the explicit flag stops the poll; legacy backends that don't send it keep
 * the time-bounded behavior.
 *
 * The outer bound stays TIME, in the caller's hot-gate
 * (isProvenanceMessageHot / isAnyProvenanceMessageHot): it auto-clears ~65s
 * after the LAST turn finalized and RESETS on each finalization. So only the
 * one finalizing turn polls (historical turns are never hot — the audit-8
 * request bomb stays fixed), even if the settled flag never arrives.
 */
export const messageProvenancePollInterval = (
  data: AssistantResponseSources | undefined,
  query: { state?: { status?: string } } | null | undefined,
): number | false => {
  if (query?.state?.status === "error") return false;
  if (data?.turn_settled === true) return false;
  return 2000;
};

/**
 * Refresh the provenance funnels when an assistant TURN settles.
 *
 * ``isTurnSettled`` must be the streaming store's ``!isTurnActive &&
 * !isLoading`` — NOT ``isEnded``. In a live agent session the per-turn
 * ``end`` SSE event only clears ``isTurnActive``/``isLoading`` (the session
 * stays open for follow-up turns); ``isEnded`` is a SESSION-terminal signal
 * that never fires at turn end and blips spuriously on reconnects. Keying the
 * refresh off ``isEnded`` meant zero provenance refetches after a live cited
 * turn — the Sources panel kept the stale pre-turn payload until a manual
 * reload (dialogue 1796: two mount POSTs, then silence).
 *
 * ``isTurnActive`` survives tool-call cycles (the ``end`` handler keeps it
 * true while a tool call is pending, #1525), so this fires exactly once per
 * turn, at the point citations are about to persist. It fires on the
 * active→settled TRANSITION only — opening an already-settled historical
 * dialogue must not refetch (the mount fetch is already fresh). If the
 * streamed message id lags the end event by a beat, the transition stays
 * pending until the id materializes.
 *
 * Citations persist a beat after the turn settles, so a single invalidate
 * can still refetch too early — re-invalidate a few times over the next
 * several seconds, paired with the poll-until-converged hot window
 * ({@link markProvenanceMessageHot}) for stragglers.
 */
export const useProvenanceTurnRefresh = (
  isTurnSettled: boolean,
  dialogueId: number | null | undefined,
  messageId: string | null | undefined,
): void => {
  const queryClient = useQueryClient();
  // Starts "handled" so an already-settled dialogue doesn't fire on mount.
  const handledRef = useRef(true);

  useEffect(() => {
    if (!isTurnSettled) {
      handledRef.current = false;
      return;
    }
    if (handledRef.current) return;
    if (dialogueId == null || !messageId) return; // transition stays pending
    handledRef.current = true;

    markProvenanceMessageHot(messageId);
    const key = ["messages", "provenance", dialogueId, messageId];
    const dialogueKey = ["messages", "provenance", "dialogue", dialogueId];
    // Three rounds, not a dense schedule: once a refetch observes the turn as
    // UNsettled, the hot poll takes over until `turn_settled` flips, so these
    // only need to bridge the persist lag. The converse engine persists in a
    // background task AFTER the stream closes, so the 0ms round can still see
    // the PREVIOUS turn's settled payload (which keeps the poll off) — the
    // later rounds re-check after that persist typically lands.
    const timers = [0, 2000, 6000].flatMap((ms) => [
      setTimeout(() => queryClient.invalidateQueries(key), ms),
      setTimeout(() => queryClient.invalidateQueries(dialogueKey), ms),
    ]);
    return () => timers.forEach(clearTimeout);
  }, [isTurnSettled, dialogueId, messageId, queryClient]);
};

/**
 * Fetch the three-tier source provenance (Searched → Read → Cited) for a
 * single assistant response.
 *
 * Mirrors the auth/api conventions of {@link fetchChunksByIds} in
 * ``ChunkServices.ts`` — a bare authenticated axios POST against
 * ``nuclearn_api_url`` returning the JSON body directly.
 *
 * @param dialogueId - The dialogue the message belongs to.
 * @param messageId - The assistant message id to fetch provenance for.
 */
export const fetchMessageProvenance = (
  dialogueId: number,
  messageId: string | null,
  scope: "message" | "dialogue" = "message",
) => {
  return axios
    .post<AssistantResponseSources>(
      `${nuclearn_api_url}/datasets/messages/provenance`,
      {
        dialogue_id: dialogueId,
        message_id: messageId,
        scope,
      },
    )
    .then((response) => {
      // Defensively backfill missing tiers so consumers can always rely on
      // arrays being present (spec §9.1 graceful degradation). Coalesce per key
      // so an explicit `null` in the payload (not just a missing key) still
      // falls back to [] — a spread alone would let `searched: null` through and
      // break `.flatMap` downstream (#20423 L3).
      const data = (response.data ?? {}) as Partial<AssistantResponseSources>;
      const merged = { ...EMPTY_PROVENANCE, ...data };
      for (const key of Object.keys(
        EMPTY_PROVENANCE,
      ) as (keyof AssistantResponseSources)[]) {
        if (merged[key] == null) {
          (merged as Record<string, unknown>)[key] = EMPTY_PROVENANCE[key];
        }
      }
      return merged;
    });
};

/** Pull a download filename out of a Content-Disposition header. */
const filenameFromDisposition = (
  disposition: string | undefined,
  fallback: string,
) => {
  const match = disposition?.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? fallback;
};

/** Trigger a browser download for an in-memory blob. */
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Fetch the provenance export for one assistant response as an in-memory blob
 * plus its filename (spec Story 6). ``json`` returns the structured audit
 * payload; ``pdf`` returns the formal Gotenberg-rendered summary streamed by the
 * backend. Callers either download it ({@link exportMessageProvenance}) or hand
 * the blob to another destination (e.g. save-to-project-folder).
 */
export const buildProvenanceExportBlob = async (
  dialogueId: number,
  messageId: string | null,
  format: "json" | "pdf",
  scope: "message" | "dialogue" = "message",
): Promise<{ blob: Blob; filename: string }> => {
  const response = await axios.post(
    `${nuclearn_api_url}/datasets/messages/provenance/export`,
    { dialogue_id: dialogueId, message_id: messageId, format, scope },
    { responseType: format === "pdf" ? "blob" : "json" },
  );

  const fallbackBase =
    scope === "dialogue"
      ? `provenance_dialogue_${dialogueId}`
      : `provenance_${dialogueId}_${messageId}`;
  if (format === "pdf") {
    return {
      blob: response.data as Blob,
      filename: filenameFromDisposition(
        response.headers?.["content-disposition"],
        `${fallbackBase}.pdf`,
      ),
    };
  }

  const json = JSON.stringify(response.data, null, 2);
  return {
    blob: new Blob([json], { type: "application/json" }),
    filename: `${fallbackBase}.json`,
  };
};

/**
 * Export the provenance audit trail for one assistant response as a browser
 * download (spec Story 6) — mirroring {@link DatasetViewerDownloadButton}'s
 * anchor-click idiom.
 */
export const exportMessageProvenance = async (
  dialogueId: number,
  messageId: string | null,
  format: "json" | "pdf",
  scope: "message" | "dialogue" = "message",
) => {
  const { blob, filename } = await buildProvenanceExportBlob(
    dialogueId,
    messageId,
    format,
    scope,
  );
  downloadBlob(blob, filename);
};

/**
 * React Query hook to fetch source provenance for an assistant message.
 *
 * @param dialogueId - The dialogue the message belongs to (null disables).
 * @param messageId - The assistant message id (null/empty disables).
 */
export const useMessageProvenance = (
  dialogueId: number | null | undefined,
  messageId: string | null | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
  },
) => {
  const canFetch = dialogueId != null && !!messageId;
  const v2Ready = useProvenanceV2Ready();
  const queryClient = useQueryClient();

  return useQuery(
    ["messages", "provenance", dialogueId, messageId],
    () => fetchMessageProvenance(dialogueId as number, messageId as string),
    {
      staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
      cacheTime: options?.cacheTime ?? 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      // Fail fast on 404 (deterministic), keep the retry budget for transient
      // errors — and never poll while errored (see the interval predicate).
      retry: shouldNotRetry404,
      // The Cited tier is persisted at turn *finalization* — AFTER the Read
      // tier (which the agent registers during its tool cycles). So a fetch
      // during streaming sees read>0 but cited=0, and the funnel would lock at
      // "0 cited" until a manual reload (#20423). Poll until a citation appears
      // — but ONLY for the HOT message (the turn whose stream just ended,
      // marked by AtomAssistPage). Historical zero-citation turns are final;
      // polling them 2s×30 each on every dialogue open was the audit-8 request
      // bomb. Same gate for `refetchOnMount: "always"`: only the hot message
      // needs the every-mount re-check; others respect staleTime.
      refetchOnMount: isProvenanceMessageHot(messageId) ? "always" : true,
      refetchInterval: (
        data: AssistantResponseSources | undefined,
        query: Parameters<typeof messageProvenancePollInterval>[1],
      ) =>
        isProvenanceMessageHot(messageId)
          ? messageProvenancePollInterval(data, query)
          : false,
      ...options,
      // Composed AFTER the options spread so a caller-supplied `enabled` ANDs
      // with — never replaces — the internal guards: the null-id guard
      // (surfaces mounted without a dialogue/message must not fire requests)
      // and the capability gate (legacy backends are never polled).
      enabled: canFetch && v2Ready && (options?.enabled ?? true),
      // Defensive schema negotiation (after the options spread so callers
      // can't accidentally drop it): a payload whose schema_version this build
      // doesn't recognize means the backend changed under us — invalidate the
      // capabilities query so a flag flip propagates within one interaction.
      onSuccess: (data: AssistantResponseSources) => {
        if (!isRecognizedProvenanceSchema(data?.schema_version)) {
          queryClient.invalidateQueries(PROVENANCE_CAPABILITIES_QUERY_KEY);
        }
      },
    },
  );
};

/**
 * React Query hook for the DIALOGUE-accumulated provenance funnel — every
 * assistant turn merged with dialogue-global citation numbers (#20423 Story 1/3).
 * Powers the session-wide Sources panel + the inline ``[N]`` numbering.
 *
 * Query key is nested under ``["messages","provenance",...]`` so the stream-end
 * invalidations in AtomAssistPage refresh it as new turns finalize.
 */
export const useDialogueProvenance = (
  dialogueId: number | null | undefined,
  options?: { enabled?: boolean; staleTime?: number; cacheTime?: number },
) => {
  const canFetch = dialogueId != null;
  const v2Ready = useProvenanceV2Ready();
  const queryClient = useQueryClient();

  return useQuery(
    ["messages", "provenance", "dialogue", dialogueId],
    () => fetchMessageProvenance(dialogueId as number, null, "dialogue"),
    {
      staleTime: options?.staleTime ?? 5 * 60 * 1000,
      cacheTime: options?.cacheTime ?? 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Respect staleTime on mount (default true) rather than "always": this
      // query is mounted by EVERY CitationLink, so "always" fired one
      // dialogue-scope refetch per streamed citation. Stream-end freshness is
      // handled by the delayed invalidations in AtomAssistPage, and onSuccess
      // still re-checks capabilities on an unrecognized schema (#20423 M6).
      refetchOnMount: true,
      retry: shouldNotRetry404,
      // Poll while a turn is finalizing (same 2s/citation-less/60s-bounded
      // shape as the message funnel) so the inline [N] numbering + Sources
      // badge fill in without a reload. The 4 fixed stream-end invalidations in
      // AtomAssistPage can all fire before the Cited tier persists on a long
      // turn, leaving the dialogue funnel stale-empty until a manual remount —
      // this is the self-heal the message funnel already had (#20423 A2).
      refetchInterval: (
        data: AssistantResponseSources | undefined,
        query: Parameters<typeof messageProvenancePollInterval>[1],
      ) =>
        isAnyProvenanceMessageHot()
          ? messageProvenancePollInterval(data, query)
          : false,
      ...options,
      // Composed AFTER the options spread so a caller-supplied `enabled`
      // (e.g. SourcesTab's v2 gate) ANDs with — never replaces — the null-id
      // guard and the internal capability gate. Before this, a caller passing
      // `enabled: true` clobbered the null-id guard and dialogue-less surfaces
      // fired 422 storms under the [..., "dialogue", undefined] key.
      enabled: canFetch && v2Ready && (options?.enabled ?? true),
      // See useMessageProvenance: unrecognized schema_version → re-check
      // capabilities so the gate reacts to a backend change without a reload.
      onSuccess: (data: AssistantResponseSources) => {
        if (!isRecognizedProvenanceSchema(data?.schema_version)) {
          queryClient.invalidateQueries(PROVENANCE_CAPABILITIES_QUERY_KEY);
        }
      },
    },
  );
};
